import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, X, Upload, RotateCcw } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { apiRequest } from '../../utils/api';
import AdminLayout from '../../components/admin/AdminLayout';
import { Star } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

export default function ReviewStandeesManagementPage() {
  const [items, setItems] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    material_pricing: [],
    sample_design_url: '',
    short_description: '',
    features: [''],
  });
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [imagePreview, setImagePreview] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      
      // Fetch review standees
      const standeesResponse = await apiRequest(`${BACKEND_URL}/api/admin/review-standees-list`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      
      // Fetch materials
      const materialsResponse = await apiRequest(`${BACKEND_URL}/api/admin/materials`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (standeesResponse.ok) setItems(standeesResponse.data.review_standees || []);
      if (materialsResponse.ok) setMaterials(materialsResponse.data.materials || []);
      
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingItem(null);
    
    setFormData({
      name: '',
      material_pricing: [],
      sample_design_url: '',
      short_description: '',
      features: [''],
    });
    setImagePreview('');
    setShowModal(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name || '',
      material_pricing: item.material_pricing || [],
      sample_design_url: item.sample_design_url || '',
      short_description: item.short_description || '',
      features: item.features && item.features.length > 0 ? item.features : [''],
    });
    setImagePreview(item.sample_design_url || '');
    setShowModal(true);
  };

  const handleDelete = async (item) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await apiRequest(`${BACKEND_URL}/api/admin/review-standees-list/${item.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (!response.ok) {
        throw new Error('Failed to deactivate item');
      }

      setDeleteConfirm(null);
      fetchData();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleRestore = async (item) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await apiRequest(`${BACKEND_URL}/api/admin/review-standees-list/${item.id}/restore`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (!response.ok) {
        throw new Error('Failed to restore item');
      }

      fetchData();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
        setFormData({
          ...formData,
          sample_design_url: reader.result,
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.material_pricing.length === 0) {
      setError('Please select at least one material');
      return;
    }

    const filteredFeatures = formData.features.filter(f => f.trim() !== '');

    try {
      const token = localStorage.getItem('admin_token');
      const method = editingItem ? 'PUT' : 'POST';
      const url = editingItem 
        ? `${BACKEND_URL}/api/admin/review-standees-list/${editingItem.id}`
        : `${BACKEND_URL}/api/admin/review-standees-list`;

      const submitData = {
        ...formData,
        features: filteredFeatures,
      };

      const response = await apiRequest(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(submitData),
      });

      if (!response.ok) {
        throw new Error(response.data?.detail || 'Failed to save item');
      }

      setShowModal(false);
      setFormData({
        name: '',
        material_pricing: [],
        sample_design_url: '',
        short_description: '',
        features: [''],
      });
      setImagePreview('');
      setEditingItem(null);
      fetchData();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleMaterialToggle = (materialId) => {
    const existing = formData.material_pricing.find(mp => mp.material_id === materialId);
    if (existing) {
      setFormData({
        ...formData,
        material_pricing: formData.material_pricing.filter(mp => mp.material_id !== materialId),
      });
    } else {
      setFormData({
        ...formData,
        material_pricing: [...formData.material_pricing, { material_id: materialId, price: 0 }],
      });
    }
  };

  const handleMaterialPriceChange = (materialId, price) => {
    setFormData({
      ...formData,
      material_pricing: formData.material_pricing.map(mp =>
        mp.material_id === materialId ? { ...mp, price: parseFloat(price) || 0 } : mp
      ),
    });
  };

  const handleFeatureChange = (index, value) => {
    const newFeatures = [...formData.features];
    newFeatures[index] = value;
    setFormData({
      ...formData,
      features: newFeatures,
    });
  };

  const handleAddFeature = () => {
    setFormData({
      ...formData,
      features: [...formData.features, ''],
    });
  };

  const handleRemoveFeature = (index) => {
    if (formData.features.length > 1) {
      setFormData({
        ...formData,
        features: formData.features.filter((_, i) => i !== index),
      });
    }
  };

  return (
    <AdminLayout>
      <div className="p-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Star className="w-8 h-8 text-navy" />
            <div>
              <h1 className="text-2xl font-bold text-navy">Review Standees</h1>
              <p className="text-sm text-gray-600">Total: {items.length} standees</p>
            </div>
          </div>
          <Button
            onClick={handleAdd}
            className="bg-navy text-white hover:bg-navy/90 rounded-full"
            data-testid="add-review-standee-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add New
          </Button>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        {/* Table */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-navy mx-auto"></div>
          </div>
        ) : items.length === 0 ? (
          <div className="bg-white rounded-2xl p-12 text-center shadow-sm">
            <Star className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-navy mb-2">No Review Standees Yet</h3>
            <p className="text-gray-600 mb-6">Click "Add New" to create your first review standee.</p>
          </div>
        ) : (
          <>
            {/* Active Standees */}
            <div className="mb-8">
              <h2 className="text-lg font-bold mb-4 flex items-center gap-2" style={{ color: '#7D64FA' }}>
                <span className="w-3 h-3 bg-green-500 rounded-full"></span>
                Active Standees ({items.filter(i => i.is_active !== false).length})
              </h2>
              <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="review-standees-table">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          Sample Design
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          Standee Name
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          Material
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {items.filter(i => i.is_active !== false).map((item) => (
                        <tr key={item.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4">
                            {item.sample_design_url ? (
                              <img 
                                src={item.sample_design_url} 
                                alt={item.name}
                                className="w-16 h-16 object-cover rounded-lg"
                              />
                            ) : (
                              <div className="w-16 h-16 bg-gray-200 rounded-lg flex items-center justify-center">
                                <Upload className="w-6 h-6 text-gray-400" />
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900 font-medium">
                            {item.name}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900">
                            {item.material_display || '-'}
                          </td>
                          <td className="px-6 py-4">
                            <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-xs font-semibold">
                              Active
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleEdit(item)}
                                className="text-blue-600 hover:text-blue-800"
                                data-testid={`edit-standee-${item.id}`}
                              >
                                <Edit2 className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => setDeleteConfirm(item)}
                                className="text-red-600 hover:text-red-800"
                                data-testid={`delete-standee-${item.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {items.filter(i => i.is_active !== false).length === 0 && (
                    <div className="text-center py-8 text-gray-500">No active standees</div>
                  )}
                </div>
              </div>
            </div>

            {/* Inactive Standees */}
            {items.filter(i => i.is_active === false).length > 0 && (
              <div>
                <h2 className="text-lg font-bold text-gray-600 mb-4 flex items-center gap-2">
                  <span className="w-3 h-3 bg-red-500 rounded-full"></span>
                  Inactive Standees ({items.filter(i => i.is_active === false).length})
                </h2>
                <div className="bg-white rounded-2xl shadow-sm overflow-hidden opacity-75">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                            Sample Design
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                            Standee Name
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                            Status
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {items.filter(i => i.is_active === false).map((item) => (
                          <tr key={item.id} className="hover:bg-gray-50 bg-gray-50/50">
                            <td className="px-6 py-4">
                              {item.sample_design_url ? (
                                <img 
                                  src={item.sample_design_url} 
                                  alt={item.name}
                                  className="w-16 h-16 object-cover rounded-lg opacity-50"
                                />
                              ) : (
                                <div className="w-16 h-16 bg-gray-200 rounded-lg flex items-center justify-center">
                                  <Upload className="w-6 h-6 text-gray-400" />
                                </div>
                              )}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-500 font-medium">
                              {item.name}
                            </td>
                            <td className="px-6 py-4">
                              <span className="px-3 py-1 bg-red-100 text-red-800 rounded-full text-xs font-semibold">
                                Inactive
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                              <button
                                onClick={() => handleRestore(item)}
                                className="text-green-600 hover:text-green-800 flex items-center gap-1"
                              >
                                <RotateCcw className="w-4 h-4" />
                                <span className="text-xs">Restore</span>
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* Add/Edit Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-navy">
                  {editingItem ? 'Edit' : 'Add'} Review Standee
                </h2>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Standee Name */}
                <div>
                  <label className="block text-sm font-semibold text-navy mb-2">
                    Review Standee Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-navy focus:border-transparent"
                    placeholder="Enter standee name (e.g., Google Review Standee)"
                    required
                    data-testid="standee-name-input"
                  />
                </div>

                {/* Materials with Checkbox and Price */}
                <div>
                  <label className="block text-sm font-semibold text-navy mb-3">
                    Standee Materials & Pricing (USD) <span className="text-red-500">*</span>
                  </label>
                  <div className="space-y-3 max-h-64 overflow-y-auto border border-gray-200 rounded-lg p-4">
                    {materials.map((material) => {
                      const selected = formData.material_pricing.find(mp => mp.material_id === material.id);
                      return (
                        <div key={material.id} className="flex items-center gap-3">
                          <input
                            type="checkbox"
                            checked={!!selected}
                            onChange={() => handleMaterialToggle(material.id)}
                            className="w-4 h-4 text-navy focus:ring-navy rounded"
                          />
                          <label className="flex-1 text-sm font-medium text-gray-700">
                            {material.name}
                          </label>
                          {selected && (
                            <div className="flex items-center gap-2">
                              <span className="text-sm text-gray-600">$</span>
                              <input
                                type="number"
                                value={selected.price || ''}
                                onChange={(e) => handleMaterialPriceChange(material.id, e.target.value)}
                                className="w-24 px-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-navy focus:border-transparent text-sm"
                                placeholder="Price"
                                min="0"
                                step="0.01"
                              />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  {formData.material_pricing.length === 0 && (
                    <p className="text-xs text-red-500 mt-2">Please select at least one material</p>
                  )}
                  <p className="text-xs text-gray-500 mt-2">All prices are in US Dollars (USD)</p>
                </div>

                {/* Short Description */}
                <div>
                  <label className="block text-sm font-semibold text-navy mb-2">
                    Short Description
                  </label>
                  <textarea
                    name="short_description"
                    value={formData.short_description}
                    onChange={handleChange}
                    rows="3"
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-navy focus:border-transparent"
                    placeholder="Enter a brief description of the standee"
                  />
                </div>

                {/* Feature List */}
                <div>
                  <label className="block text-sm font-semibold text-navy mb-2">
                    Features
                  </label>
                  <div className="space-y-2">
                    {formData.features.map((feature, index) => (
                      <div key={index} className="flex gap-2">
                        <input
                          type="text"
                          value={feature}
                          onChange={(e) => handleFeatureChange(index, e.target.value)}
                          className="flex-1 px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-navy focus:border-transparent"
                          placeholder={`Feature ${index + 1}`}
                        />
                        {formData.features.length > 1 && (
                          <button
                            type="button"
                            onClick={() => handleRemoveFeature(index)}
                            className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={handleAddFeature}
                      className="text-navy hover:text-navy/80 text-sm font-medium flex items-center gap-2"
                    >
                      <Plus className="w-4 h-4" />
                      Add More Feature
                    </button>
                  </div>
                </div>

                {/* Sample Design */}
                <div>
                  <label className="block text-sm font-semibold text-navy mb-2">
                    Sample Design
                  </label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                    {imagePreview ? (
                      <div className="space-y-4">
                        <img 
                          src={imagePreview} 
                          alt="Preview" 
                          className="max-w-full max-h-48 mx-auto rounded-lg"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            setImagePreview('');
                            setFormData({ ...formData, sample_design_url: '' });
                          }}
                          className="text-red-600 hover:text-red-800 text-sm"
                        >
                          Remove Image
                        </button>
                      </div>
                    ) : (
                      <div>
                        <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                        <label className="cursor-pointer">
                          <span className="text-navy font-medium hover:underline">
                            Click to upload
                          </span>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleImageChange}
                            className="hidden"
                          />
                        </label>
                        <p className="text-xs text-gray-500 mt-2">
                          PNG, JPG, JPEG up to 5MB
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  <Button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="flex-1 bg-gray-200 text-navy hover:bg-gray-300 rounded-full"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1 bg-navy text-white hover:bg-navy/90 rounded-full"
                    data-testid="submit-standee-btn"
                  >
                    {editingItem ? 'Update' : 'Create'}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Delete Confirmation */}
        {deleteConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl max-w-md w-full p-6">
              <h2 className="text-xl font-bold text-navy mb-4">Confirm Deactivate</h2>
              <p className="text-gray-600 mb-6">
                Are you sure you want to deactivate "{deleteConfirm.name}"? It will be moved to the inactive list and can be restored later.
              </p>
              <div className="flex gap-3">
                <Button
                  onClick={() => setDeleteConfirm(null)}
                  className="flex-1 bg-gray-200 text-navy hover:bg-gray-300 rounded-full"
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => handleDelete(deleteConfirm)}
                  className="flex-1 bg-red-500 text-white hover:bg-red-600 rounded-full"
                  data-testid="confirm-delete-btn"
                >
                  Deactivate
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
